/**
 * Every sentence the Tax Profile page shows about where a value came from and how far a document
 * has got.
 *
 * The api sends a document's row and a value's source as database columns. This file is the only
 * place that turns those columns into the sentences the page shows, and it holds most of the page's
 * other words too. Some components still write words of their own: the Add document modal
 * (AddDocumentModal.tsx) and the Choose a document type modal (ChooseDocumentTypeModal.tsx) keep
 * their headings, labels and sentences as constants at the top of their files, and DocumentsTable,
 * ProfileGroup, SectionIndex, DocumentActivity and TaxProfile.tsx write a few headings, column
 * headers and labels inside their JSX (TaxProfile.tsx also two fallback error sentences).
 *
 * None of these sentences uses our own words for reading a document. They never say extraction,
 * promotion, provenance, field source, kind, register, queued or confidence, and they carry no
 * status codes and no costs.
 */

import {
  OcrDocumentType,
  TAX_DOCUMENT_KIND_LABELS,
  TAX_DOCUMENT_MAX_FILE_BYTES,
  TAX_DOCUMENT_MAX_READ_ATTEMPTS,
  TAX_DOCUMENT_MAX_SPREADSHEET_BYTES,
  taxDocumentFileIsReadable,
  taxDocumentMaxFileBytes,
  taxDocumentFileIsSpreadsheet,
} from '@shared/constants/taxProfile';
import type {
  AnswerTaxDocumentEntityRequest,
  TaxDocumentEntityHold,
  TaxDocumentListItem,
  TaxDocumentReviewLine,
  TaxDocumentValueStatus,
  TaxProfileApprovedValue,
  TaxProfileListName,
  TaxProfileFieldValue,
  TaxProfileValueSource,
} from '@shared/types/taxProfile';
import { FOREIGN_PERSON_LABELS } from '@shared/constants/taxProfileFields';
import { taxDocumentTitle } from '@shared/utils/taxDocumentName';
import type { TaxDocumentAddFailureReason } from '@/hooks/useTaxProfile';
import {
  formatDateTime,
  formatTimestampDate,
  formatPersonName,
} from './format';
import { newestWaiting } from './fieldState';

/** The largest file the profile accepts, rounded down to whole megabytes for the sentences here. */
export const MAX_FILE_MB = Math.floor(
  TAX_DOCUMENT_MAX_FILE_BYTES / (1024 * 1024)
);

/** The largest spreadsheet the profile accepts, in whole megabytes, for the sentences here. */
export const MAX_SPREADSHEET_MB = Math.floor(
  TAX_DOCUMENT_MAX_SPREADSHEET_BYTES / (1024 * 1024)
);

/**
 * What the Add document modal says beside a file above the limit taxDocumentMaxFileBytes gives its
 * name: on the row of such a file in the pick tab, whose box cannot be ticked, and in the list of
 * files chosen from the computer that it held back.
 */
export const tooLargeToReadLabel = (fileName: string): string =>
  `Too large to read (over ${Math.floor(
    taxDocumentMaxFileBytes(fileName) / (1024 * 1024)
  )} MB)`;

/**
 * How to bring a file under the limit taxDocumentMaxFileBytes gives its name, said after the files
 * the Add document modal held back for their size. The Acrobat steps are for a PDF only; a
 * spreadsheet is brought under its limit by keeping only the sheets that matter, and an image or a
 * Word document by saving it again smaller.
 */
export const tooLargeAdvice = (fileNames: string[]): string => {
  const extension = (name: string) =>
    name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  const advice: string[] = [];
  if (fileNames.some((name) => extension(name) === 'pdf')) {
    advice.push(
      'Reduce a PDF’s size (in Acrobat, File > Reduce File Size) or split it, for example the ' +
        'return and its K-1 pages as separate files, and add the parts.'
    );
  }
  if (fileNames.some(taxDocumentFileIsSpreadsheet)) {
    advice.push(
      'For a spreadsheet, save a copy with only the sheets we need, such as the cap table.'
    );
  }
  if (
    fileNames.some(
      (name) => extension(name) !== 'pdf' && !taxDocumentFileIsSpreadsheet(name)
    )
  ) {
    advice.push('Save any other file again at a smaller size.');
  }
  return advice.join(' ');
};

/** The file types a document can be read from, named in every refusal about a file's type. */
export const UNSUPPORTED_FILE_SENTENCE =
  'We read PDFs, images and spreadsheets saved as .xlsx or .csv';

/** What the Add document modal says about a file whose type the profile does not store at all. */
export const CANNOT_ADD_FILE_SENTENCE =
  'We can add PDFs, images, spreadsheets and Word documents';

/** How many refused files cannotAddFilesSentence names before it says "and N more". */
const NAMED_FILES_AT_MOST = 5;

/**
 * What the Add document modal says when it leaves files out for their type, as one line: the
 * reason once, then the files' names, so that among several chosen files the person can tell which
 * ones were left out, then the types the modal does take. Past NAMED_FILES_AT_MOST files the rest
 * are counted, not named, so ticking a folder of unsupported files does not push the modal's
 * buttons off the screen.
 */
export const cannotAddFilesSentence = (fileNames: string[]): string => {
  const named = fileNames.slice(0, NAMED_FILES_AT_MOST);
  const more = fileNames.length - named.length;
  let list = named.join(', ');
  if (more > 0) list += ` and ${more} more`;
  return `Not a type we can add: ${list}. ${CANNOT_ADD_FILE_SENTENCE}.`;
};

/**
 * The line under an "On file" badge on a .xls or a Word document of a type the profile reads.
 *
 * It is built from UNSUPPORTED_FILE_SENTENCE above, so the sentence that tells a person a file was
 * not read and the sentence that tells them what to ask the client for name the same file types.
 */
export const FILED_ONLY_SENTENCE = `${UNSUPPORTED_FILE_SENTENCE}. Add an export in one of those to have it read.`;

/** What the row says for eight seconds after a value is typed, which it offers to undo. */
export const SAVED_NOTICE = 'Saved';
/** What the row says for eight seconds after one of the values found was accepted. */
export const ACCEPTED_NOTICE = 'Accepted';
/** What the row says for eight seconds after a person kept the approved value. */
export const KEPT_NOTICE = 'Kept the current value';

/** What the toast says when the document a value came from could not be opened. */
export const DOCUMENT_COULD_NOT_BE_OPENED = 'The file could not be opened';

/** The accessible name of a document's name drawn as a link, which opens that document. */
export const openDocumentLabel = (documentTitleText: string): string =>
  `Open ${documentTitleText}`;

/** The words of the window that previews a document's file on the page (DocumentPreviewModal). */
export const DOCUMENT_PREVIEW_WORDS = {
  openInNewTab: 'Open in a new tab',
  loading: 'Loading the preview',
  failed:
    'The preview could not be loaded. You can still open the file in a new tab.',
} as const;

/** The accessible name of that window: "Preview of <document>". */
export const documentPreviewLabel = (documentTitleText: string): string =>
  `Preview of ${documentTitleText}`;

/** The sentence above the sections, which says what the profile holds and how a line is decided. */
export const PROFILE_HINT =
  'The profile holds only values a person approved. A line with a yellow left edge ' +
  'needs your review: click its value to see every value we found, where each one came from, ' +
  'and pick one or type your own.';

/** Beside PROFILE_HINT: how many values wait for a person. */
export const waitingForDecisionSentence = (count: number) =>
  `Needs review: ${count}`;

const valuesCount = (count: number) =>
  count === 1 ? '1 value' : `${count} values`;

/**
 * The words of the toggle at the top of the page, which switches between the Profile view (the
 * values only, ProfileView.tsx) and the Review view (everything else on the page), and of the
 * button on the Profile view's count line, which switches to the Review view.
 */
export const VIEW_WORDS = {
  /** The accessible name of the toggle. */
  label: 'Show the profile or review it',
  profile: 'Profile',
  review: 'Review',
  reviewThem: 'Review them',
} as const;

/** Above the Profile view's cards: how many values wait for a person, as waitingForDecisionSentence counts them. */
export const notReviewedLine = (count: number) => `Not reviewed yet: ${count}`;

/**
 * The button above the Approval column that accepts every value whose sources give one value.
 *
 * `leavesOutTableRows` is true while a row of the owners, officers, subsidiaries or state
 * registrations tables waits for a person. Those rows are reviewed only with their own Finish review
 * button, so the button's count says it does not include them.
 */
export const acceptAgreeingLabel = (
  count: number,
  leavesOutTableRows = false
) =>
  `Accept every value where all sources agree (${count}${
    leavesOutTableRows ? ', tables not included' : ''
  })`;

/** What that button asks before it sends anything. */
export const acceptAgreeingQuestion = (count: number) =>
  count === 1
    ? 'Accept 1 value where all sources agree?'
    : `Accept ${count} values where all sources agree?`;

/** The two buttons that answer acceptAgreeingQuestion. */
export const YES_ACCEPT = 'Yes, accept';
export const CANCEL = 'Cancel';

/** What the toast says after that button's request, naming how many values it accepted. */
export const acceptedCountNotice = (count: number) =>
  `${valuesCount(count)} accepted`;

/**
 * What one toast says about the values the accept-all button did not accept because somebody else
 * changed them while the page was open. Expects the labels of those values.
 */
export const bulkConflictSentence = (labels: string[]) =>
  `Someone changed ${labels.join(', ')} while this page was open, so ${
    labels.length === 1 ? 'it was' : 'they were'
  } not accepted. The page now shows the current ${
    labels.length === 1 ? 'value' : 'values'
  }.`;

/** What the toast says when a write lands on a value somebody else has changed. */
export const conflictSentence = (currentValueText: string) =>
  `Someone changed this value to ${currentValueText}`;

/** The two buttons of that toast: drop what was typed, or write it over the other person's value. */
export const CONFLICT_WORDS = {
  keepTheirs: 'Keep theirs',
  overwrite: 'Overwrite',
} as const;

/**
 * The line near the top of the page when a background refresh of the profile or the documents
 * list failed while the page already shows an earlier answer. It goes once a later refresh works.
 */
export const REFRESH_FAILED_SENTENCE =
  "We couldn't refresh the page. What you see may be out of date; it will retry on its own.";

/**
 * What the page says when a person switches the tax year or the legal entity, or leaves the page,
 * while a row of the owners, officers, subsidiaries or state registrations tables has values
 * chosen that Confirm review has not saved yet.
 */
export const UNSAVED_CHOICES_SENTENCE =
  "You have values you haven't saved. Press Confirm review on the row to save them.";

/** Why a file the Add document modal sent was not attached, by the reason the api answers with. */
export const ADD_DOCUMENT_SKIP_SENTENCES: Readonly<Record<string, string>> = {
  unsupported_type:
    'we read PDFs, images and spreadsheets saved as .xlsx or .csv',
  already_attached: 'it is already attached',
  team_mismatch: 'it is not in this client’s team',
  upload_failed: 'the upload did not go through',
  duplicate_content: 'the same file is already attached',
  too_large:
    `it is over the size we read (${MAX_FILE_MB} MB, ` +
    `or ${MAX_SPREADSHEET_MB} MB for a spreadsheet)`,
};

/**
 * Why a file the Add document modal sent could not be added, by TaxDocumentAddFailureReason in
 * hooks/useTaxProfile.ts. Each one is said in brackets after the file's name, before "Try it
 * again."
 */
export const ADD_DOCUMENT_FAIL_SENTENCES: Readonly<
  Record<TaxDocumentAddFailureReason, string>
> = {
  connection_dropped:
    'the connection dropped before we heard back, so check the documents list for it first',
  busy: 'the server was busy',
  server_error: 'something went wrong on our side',
  upload_unconfirmed:
    'the connection dropped before we heard back, so check the documents list for it first',
};

/**
 * What a calculated value was inferred from, by catalog field key, for the sentences about where a
 * value came from, which read "Inferred from <this>". Both rules are in
 * api/src/taxProfile/delawareFranchiseTax.ts.
 */
export const DERIVED_RULE_SENTENCES: Readonly<Record<string, string>> = {
  de_franchise_tax_obligation: 'the state of incorporation',
  de_franchise_tax_method: 'the entity type',
};

/**
 * One piece of a sentence about where a value came from. A piece with a documentId is the name of
 * a document, which FieldRow and ValuePicker draw as a link that opens that document; every other
 * piece is plain text, the onboarding form's name included.
 */
export type SourcePiece = {
  text: string;
  documentId: number | null;
  /**
   * True on the ', ' between two sources' names (joinedNames), where SourceText with `stacked`
   * starts a new line instead of printing the comma.
   */
  separator?: true;
};

const plainPiece = (text: string): SourcePiece => ({ text, documentId: null });

/**
 * What a source is called inside a sentence: a document's name, or the words for the others. A
 * calculated source is named by what it was inferred from, with "(inferred)" after it: "the state
 * of incorporation (inferred)". A sentence naming calculated sources only says "Inferred from the
 * state of incorporation" instead (inferredOnly below).
 */
const sourceNamePiece = (
  source: TaxProfileValueSource,
  fieldKey: string
): SourcePiece => {
  switch (source.kind) {
    case 'document':
      return {
        text: source.documentName ?? 'a document',
        documentId: source.documentId,
      };
    case 'typeform':
      return plainPiece('the onboarding form');
    case 'derived': {
      const rule = DERIVED_RULE_SENTENCES[fieldKey];
      return plainPiece(rule ? `${rule} (inferred)` : 'an inferred value');
    }
    case 'human':
      return plainPiece('a value typed in by hand');
    case 'connector':
    default:
      return plainPiece(source.sourceLabel ?? 'a connected system');
  }
};

/**
 * The names of several sources joined by commas, each source once: two answers of one document
 * name that document once.
 */
const joinedNames = (
  sources: TaxProfileValueSource[],
  fieldKey: string
): SourcePiece[] => {
  const seen = new Set<string>();
  const names = sources
    .map((source) => sourceNamePiece(source, fieldKey))
    .filter((piece) => {
      const id = piece.documentId != null ? `d${piece.documentId}` : piece.text;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  return names.flatMap((piece, index) =>
    index === 0
      ? [piece]
      : [{ ...plainPiece(', '), separator: true as const }, piece]
  );
};

/**
 * "Inferred from <rule>" (DERIVED_RULE_SENTENCES), or "Inferred" for a field with no rule there,
 * when every one of `sources` is calculated; null when any of them is not.
 */
const inferredOnly = (
  sources: TaxProfileValueSource[],
  fieldKey: string
): SourcePiece | null => {
  if (!sources.every((source) => source.kind === 'derived')) return null;
  const rule = DERIVED_RULE_SENTENCES[fieldKey];
  return plainPiece(rule ? `Inferred from ${rule}` : 'Inferred');
};

/**
 * The Where it came from column of a value.
 *
 * The sentence says where the value shown on the line (shownValue in fieldState.ts) came from,
 * never where another value came from. An approved value and a changed value both show the approved
 * value, so both name the approved row's source and every open candidate that agrees with it; the
 * different value of a changed line is named with its sources in ValuePicker, and the tag in the
 * column before this one (waitingPill, "1 new value") says there is one. A proposed value
 * shows the newest waiting value, so it names that value's sources only; the tag says when the
 * sources gave several values. A value approved by typing it says "Entered by hand". An empty value
 * says nothing.
 */
export const whereItCameFrom = (
  field: TaxProfileFieldValue,
  fieldKey: string
): SourcePiece[] => {
  const from = (sources: TaxProfileValueSource[]): SourcePiece[] => {
    if (sources.length === 0) return [];
    const inferred = inferredOnly(sources, fieldKey);
    if (inferred) return [inferred];
    return [plainPiece('From '), ...joinedNames(sources, fieldKey)];
  };

  switch (field.status) {
    case 'approved':
    case 'changed': {
      const { approved, agreeing } = field;
      if (!approved) return [];
      if (approved.kind === 'human') {
        return agreeing.length === 0
          ? [plainPiece('Entered by hand')]
          : [
              plainPiece('Entered by hand, also in '),
              ...joinedNames(agreeing, fieldKey),
            ];
      }
      return from([approved, ...agreeing]);
    }
    case 'proposed':
      return from(newestWaiting(field)?.sources ?? []);
    case 'empty':
    default:
      return [];
  }
};

/**
 * The tooltip of the icon after a value nobody has reviewed yet ('proposed'), in the Profile view:
 * "Not reviewed yet. From EIN Letter - 2026." The source is named as the Where it came from column
 * names it (whereItCameFrom above); "Not reviewed yet." alone when it names none.
 */
export const notReviewedTip = (
  field: TaxProfileFieldValue,
  fieldKey: string
): string => {
  // The tooltip has no links, so two documents with the same name read as one name here, where
  // the Where it came from column keeps one link per document.
  const pieces = whereItCameFrom(field, fieldKey);
  const names = [
    ...new Set(
      pieces
        .slice(1)
        .filter((piece) => !piece.separator)
        .map((p) => p.text)
    ),
  ];
  const from =
    pieces.length > 1
      ? `${pieces[0].text}${names.join(', ')}`
      : pieces.map((piece) => piece.text).join('');
  return from ? `Not reviewed yet. ${from}.` : 'Not reviewed yet.';
};

/**
 * The tooltip of the icon after an approved value a source gave a different value for ('changed'),
 * in the Profile view.
 */
/** The heading of the Profile view's box holding everything the year switch changes. */
export const TAX_YEAR_BOX_HEADING = 'By tax year';

export const DIFFERENT_VALUE_WAITING_TIP =
  'A different value is waiting for review.';

/**
 * One source's line under a value in ValuePicker: "From <document> · tax year 2024 · added Sep 16,
 * 2026", "From the onboarding form · submitted Aug 2, 2026", "Inferred from the state of
 * incorporation · added …".
 */
export const sourceLine = (
  source: TaxProfileValueSource,
  fieldKey: string
): SourcePiece[] => {
  const when = source.submittedAt
    ? formatTimestampDate(source.submittedAt)
    : null;
  switch (source.kind) {
    case 'typeform':
      return [
        plainPiece(
          `From the onboarding form${when ? ` · submitted ${when}` : ''}`
        ),
      ];
    case 'derived': {
      const rule = DERIVED_RULE_SENTENCES[fieldKey];
      return [
        plainPiece(
          `${rule ? `Inferred from ${rule}` : 'Inferred'}${
            when ? ` · added ${when}` : ''
          }`
        ),
      ];
    }
    case 'human':
      return [plainPiece(`Entered by hand${when ? ` · added ${when}` : ''}`)];
    case 'document': {
      const rest = [
        source.taxYear ? `tax year ${source.taxYear}` : null,
        when ? `added ${when}` : null,
      ].filter((part): part is string => part !== null);
      return [
        plainPiece('From '),
        sourceNamePiece(source, fieldKey),
        ...(rest.length ? [plainPiece(` · ${rest.join(' · ')}`)] : []),
      ];
    }
    case 'connector':
    default:
      return [
        plainPiece(
          `From ${source.sourceLabel ?? 'a connected system'}${
            when ? ` · added ${when}` : ''
          }`
        ),
      ];
  }
};

/** "Maria Lopez on Sep 12, 2026", or whichever of the two is known; '' when neither is. */
const personAndDate = (
  person: Parameters<typeof formatPersonName>[0],
  date: string | null
) => {
  const name = formatPersonName(person);
  const when = date ? formatTimestampDate(date) : null;
  if (name && when) return `${name} on ${when}`;
  if (name) return name;
  if (when) return `on ${when}`;
  return '';
};

/**
 * The line under the Current option of ValuePicker: who approved the value and when, then the
 * open candidates that agree with it.
 */
export const currentValueLine = (
  field: TaxProfileFieldValue,
  fieldKey: string
): SourcePiece[] => {
  const { approved, agreeing } = field;
  if (!approved) return [];
  const who = personAndDate(approved.approvedBy, approved.approvedAt);
  const head = `From the profile${who ? ` · approved by ${who}` : ''}`;
  if (agreeing.length === 0) return [plainPiece(head)];
  return [plainPiece(`${head}, also in `), ...joinedNames(agreeing, fieldKey)];
};

/** The Approval column of an approved value, beside the green check. */
export const approvedSentence = (approved: TaxProfileApprovedValue): string => {
  const who = personAndDate(approved.approvedBy, approved.approvedAt);
  return who ? `Approved by ${who}` : 'Approved';
};

/**
 * What a value that waits for a person is, in words: "Proposed", "Proposed · 2 values", "Changed · 1
 * new value". Null for an approved or an empty value. FieldRow puts it in the accessible name of
 * the value's button, which is how a screen reader hears what the yellow left edge shows.
 */
export const waitingTag = (field: TaxProfileFieldValue): string | null => {
  const count = field.waiting.length;
  if (field.status === 'proposed') {
    return count > 1 ? `Proposed · ${count} values` : 'Proposed';
  }
  if (field.status === 'changed') {
    return `Changed · ${count} new ${count === 1 ? 'value' : 'values'}`;
  }
  return null;
};

/**
 * The yellow tag in the column after a value, drawn only where it says something the line does
 * not: "2 values" on a proposed value whose sources gave more than one value, "1 new value" on a
 * changed value, beside the Review conflict button that says the line is changed. Null for a proposed value with one waiting value, whose yellow left
 * edge, italic value and Accept button already say it waits, and for an approved or empty value.
 */
export const waitingPill = (field: TaxProfileFieldValue): string | null => {
  const count = field.waiting.length;
  if (field.status === 'proposed') {
    return count > 1 ? `${count} values` : null;
  }
  if (field.status === 'changed') {
    return `${count} new ${count === 1 ? 'value' : 'values'}`;
  }
  return null;
};

/** The first sentence of ValuePicker, which says what the choices are. */
export const pickerHint = (field: TaxProfileFieldValue): string => {
  switch (field.status) {
    case 'changed': {
      // A taxpayer number approved as its last four only, and a document with the full number
      // ending in those four: the new value completes the approved one rather than contradicting
      // it. The api sends four digits bare and nine as '*****' and the last four
      // (formatTaxpayerNumber in format.ts).
      const approvedText = String(field.approved?.value ?? '');
      const newestText = field.waiting[0]?.value ?? '';
      if (
        /^\d{4}$/.test(approvedText) &&
        newestText === `*****${approvedText}`
      ) {
        return (
          'A document has the full number for the last four digits on file. ' +
          'Keep the last four, or pick the full number.'
        );
      }
      const who =
        field.waiting[0]?.sources[0]?.kind === 'document'
          ? 'A document'
          : 'Another source';
      return `${who} says something different from the approved value. Keep the current value, or pick the new one.`;
    }
    case 'proposed':
      if (field.waiting.length > 1) {
        return 'Sources disagree. The newest is selected; pick the one to accept.';
      }
      return (field.waiting[0]?.sources.length ?? 0) > 1
        ? 'Every source gave this value. Accept it or enter a different one.'
        : 'One source gave this value. Accept it or enter a different one.';
    case 'approved':
      return 'This is the approved value. Keep it, or enter a different one.';
    case 'empty':
    default:
      return 'Nothing has given this value yet. Enter it to add it to the profile.';
  }
};

/** Under a value found in a source that did not pass one of the checks a value is read with. */
export const checkFailedSentence = (reason: string): string =>
  `This value did not pass a check: ${reason}.`;

/**
 * The words of the owners, officers, subsidiaries and state registrations tables, where a row is
 * reviewed as a whole: a choice made in a cell's ValuePicker is kept on the page until the row's
 * Confirm review button sends every choice of the row in one request.
 */
export const ROW_WORDS = {
  finishReview: 'Finish review',
  confirmReview: 'Confirm review',
  cancel: 'Cancel',
  /** ValuePicker's green button in such a table, which records the choice without writing it. */
  useThisValue: 'Use this value',
  /** The tag of a cell whose approved value a newer candidate differs from. */
  changed: 'Changed',
  /** The tag of such a cell once a person chose to keep the approved value. */
  kept: 'Kept',
  /** The tag of a cell a person typed a value into, which Confirm review writes. */
  edited: 'Edited',
  total: 'Total',
} as const;

/** The line under ValuePicker's first sentence in such a table, saying nothing is written yet. */
export const ROW_PICKER_NOTE =
  "Nothing is saved until you confirm this row's review.";

/** The noun of a row of each table, for the line under a new row's name. */
export type ListRowNoun =
  | 'owner'
  | 'officer'
  | 'subsidiary'
  | 'state registration';

/**
 * The noun of a row of each of the page's four lists, which the line under a new row's name, the
 * kind on a summary card (SUMMARY_CARD_KINDS) and listRowElementId in ListTable.tsx are built from.
 */
export const LIST_ROW_NOUNS: Readonly<Record<TaxProfileListName, ListRowNoun>> =
  {
    owners: 'owner',
    officers: 'officer',
    subsidiaries: 'subsidiary',
    stateTaxIds: 'state registration',
  };

/** What an empty cell of a list table, and an empty value on a document's summary screen, shows. */
export const EMPTY_CELL_DASH = '—';

/**
 * The words of the window an owner's or an officer's name opens, where the row is reviewed: every
 * cell of the row, in the two lists below, and the review buttons (ListTable's personDetails).
 */
export const PERSON_DETAILS_WORDS = {
  /** The heading of the cells of the columns the table draws. */
  inTable: 'In the table',
  /** The heading of the taxpayer number and the cells the table does not draw. */
  details: 'Details',
  /** The window's only button on a row with nothing waiting. */
  close: 'Close',
  /**
   * The red line under the name in the row once the window's Confirm review had cells the api
   * refused and the window was closed; it opens the window, where each refusal is under its cell.
   */
  refused: 'Some values could not be saved. Open the review to see why.',
} as const;

/**
 * The words of the button at the foot of that window that removes the owner or the officer, with
 * every value on file for them (removeListRow in useProfileFieldWrites.tsx), and of the question it
 * asks in place before anything is sent.
 */
export const REMOVE_PERSON_WORDS = {
  /** The answer to the question that goes back to the window without removing anything. */
  cancel: 'Cancel',
  /** The answer to the question that sends the removal. */
  confirm: 'Remove',
  /** Shown under the buttons when the removal failed and the api gave no sentence of its own. */
  failed: 'This could not be removed. Please try again.',
} as const;

/** The button that asks the question below: "Remove owner", "Remove officer". */
export const removePersonButton = (noun: ListRowNoun): string =>
  `Remove ${noun}`;

/**
 * The question the button asks in place of itself: "Remove Dana Reyes and every value on file for
 * them? This can't be undone."
 */
export const removePersonQuestion = (name: string): string =>
  `Remove ${name} and every value on file for them? This can't be undone.`;

/** The line under the person's name in that window: "Owner details", "Officer details". */
export const personDetailsHeading = (noun: ListRowNoun): string =>
  `${noun.charAt(0).toUpperCase()}${noun.slice(1)} details`;

/**
 * The line under the name in the row when cells only the window shows wait for a person:
 * "1 more value to review", "2 more values to review".
 */
export const moreToReviewLine = (count: number): string =>
  `${count} more ${count === 1 ? 'value' : 'values'} to review`;

/**
 * The line under the name of a row nobody has approved yet: "New owner, found in <document>", the
 * document being the source of the name cell's newest candidate; "New owner" when it has none.
 */
export const newRowLine = (
  noun: ListRowNoun,
  source: TaxProfileValueSource | null,
  fieldKey: string
): SourcePiece[] => {
  const lead = `New ${noun}`;
  if (!source) return [plainPiece(lead)];
  return [plainPiece(`${lead}, found in `), sourceNamePiece(source, fieldKey)];
};

/** The Approval cell of a table row nobody needs to look at: who approved it and when. */
export const reviewedSentence = (
  approved: Pick<TaxProfileApprovedValue, 'approvedBy' | 'approvedAt'>
): string => {
  const who = personAndDate(approved.approvedBy, approved.approvedAt);
  return who ? `Reviewed by ${who}` : 'Reviewed';
};

/** The note at the right of the owners table's Total row. */
export const TOTAL_WAITING_NOTE = 'Counts values that still need review';
export const TOTAL_REVIEWED_NOTE = 'All values reviewed';

/**
 * A total of the owners' shares as the Total row prints it: "100%", or "98.5%, not 100%" when it is
 * more than half a point away from 100.
 */
export const shareTotalText = (formattedTotal: string, isOff: boolean) =>
  isOff ? `${formattedTotal}, not 100%` : formattedTotal;

/** An officer's compensation for a tax year other than the one the page shows: "$120,000 (2023)". */
export const amountWithYear = (amountText: string, taxYear: number) =>
  `${amountText} (${taxYear})`;

/**
 * The icon of the owners table's US person column, read off the owner_foreign_person cell. The
 * first two are the column's booleanLabels (FOREIGN_PERSON_LABELS), which the picker's options and
 * the cell's text use, so the icon's tooltip and the words say the same thing.
 */
export const US_PERSON_WORDS = {
  domestic: FOREIGN_PERSON_LABELS.false,
  foreign: FOREIGN_PERSON_LABELS.true,
  unknown: "We don't know yet whether this owner is a US person.",
} as const;

/** What each table says when there is nothing to list, or the filter left nothing on screen. */
export const LIST_EMPTY_SENTENCES = {
  owners: 'No owners on file.',
  officers: 'No officers on file.',
  subsidiariesAndStates: 'No subsidiaries or state registrations on file.',
  filteredOut: 'No rows in this table match the filters.',
} as const;

/** What a section of values (ProfileGroup) says when the filters left none of its values on screen. */
export const SECTION_FILTERED_OUT_SENTENCE =
  'No values in this section match the filters.';

/** The words of the value line and of ValuePicker that are not sentences about a source. */
export const VALUE_WORDS = {
  accept: 'Accept',
  chooseValue: 'Choose value',
  reviewConflict: 'Review conflict',
  approved: 'Approved',
  current: 'Current',
  newest: 'Newest',
  /** The tag of the option on a document's summary screen that came from that document. */
  thisDocument: 'This document',
  enterDifferent: 'Enter a different value',
  enterValue: 'Enter a value',
  acceptValue: 'Accept value',
  keepCurrent: 'Keep current value',
  cancel: 'Cancel',
  undo: 'Undo',
  /** The placeholder of the box a taxpayer number is typed into. */
  taxpayerNumberPlaceholder: '9 digits',
  /** Under that box while it holds some digits but not nine; the green button stays disabled. */
  taxpayerNumberNeedsNine: 'Type all nine digits',
} as const;

/**
 * What the Add document modal's list of document types calls OcrDocumentType.UNKNOWN.
 *
 * TAX_DOCUMENT_KIND_LABELS says 'Document' for that type, which is also the title the documents
 * table gives such a row. The words in the parenthesis are here because a document of that type is
 * filed and never sent to be read, which is what a person picking it is choosing.
 */
export const UNKNOWN_KIND_OPTION = 'Document (stored, not read)';

/** The codes a second reading can still succeed on: their rows offer Try again. */
export const RETRYABLE_ERROR_CODES = ['retries_exhausted', 'enqueue_failed'];

/**
 * True when the row's doc_type column holds one of the types a document can be read as.
 *
 * OcrDocumentType.UNKNOWN is not one of them. It is a person saying the file is none of those
 * types, and sendForReading in api/src/taxProfile/dashboard/addDocuments.ts files such a document
 * instead of sending it.
 */
const typeHasBeenChosen = (
  document: Pick<TaxDocumentListItem, 'doc_type'>
): boolean =>
  document.doc_type !== null && document.doc_type !== OcrDocumentType.UNKNOWN;

/**
 * The line under the badge of a document that was added without a type and has not been sent to be
 * read yet.
 */
export const TYPE_WORKED_OUT_FROM_THE_FILE =
  'No type was chosen for this document, so we pick one automatically from the file itself.';

/**
 * Why a document could not be read, one sentence per extraction_error code.
 *
 * Expects the row's extraction_error and doc_type columns. doc_type names the type in the sentence
 * about a file that does not look like what it was read as.
 *
 * Returns the sentence the row shows under its badge.
 */
export const readFailureSentence = (
  document: Pick<TaxDocumentListItem, 'extraction_error' | 'doc_type'>
): string => {
  const kindLabel = document.doc_type
    ? TAX_DOCUMENT_KIND_LABELS[document.doc_type]
    : 'document';
  switch (document.extraction_error) {
    case 'locked_pdf':
      return 'This PDF is password protected. Ask the client for an unlocked copy.';
    case 'unreadable_pdf':
      return 'We could not open this PDF. Ask the client for a new copy of the file.';
    case 'unsupported_type':
      return `${UNSUPPORTED_FILE_SENTENCE}.`;
    case 'wrong_document_kind':
      return `This does not look like a ${kindLabel}.`;
    case 'empty_file':
      return 'The file is empty.';
    case 'too_large':
      return (
        `The file is larger than ${MAX_FILE_MB} MB, more than we can read in one go. ` +
        'Reduce its size (in Acrobat, File > Reduce File Size, or export it again from the tax ' +
        'software) or split it, for example the return and its K-1 pages as separate files, and ' +
        'add the parts.'
      );
    case 'too_many_pages':
      return (
        'The document has more than 1,000 pages, more than we can read in one go. Split it, ' +
        'for example the return and its K-1 pages as separate files, and add the parts.'
      );
    case 'spreadsheet_too_large':
      return (
        `This spreadsheet is larger than we read at once (over ${MAX_SPREADSHEET_MB} MB, or too ` +
        'many rows). Ask the client for an export with just the cap table, or a PDF of it.'
      );
    case 'unreadable_spreadsheet':
      return (
        'This spreadsheet could not be opened. It may be password protected or damaged. Ask ' +
        'the client for an unlocked .xlsx or a PDF export.'
      );
    case 'unknown_document_kind':
      return 'We could not tell what this document is. Press Add document type and choose one.';
    case 'retries_exhausted':
    case 'enqueue_failed':
      return 'We could not read this file. Try again.';
    default:
      return 'We could not read this file.';
  }
};

/**
 * The three things a row's own button can do.
 *
 * 'read' and 'retry' send the document's id to the read route on their own. 'choose-type' opens
 * ChooseDocumentTypeModal first and sends the type the person picked there with that id, because
 * this document's reading already failed for want of a type and sending the id alone would fail it
 * the same way again.
 */
export type TaxDocumentAction = 'read' | 'retry' | 'choose-type';

export type TaxDocumentStatusWording = {
  /** The badge's text. */
  text: string;
  /** The daisyUI badge modifier the text is shown in. */
  badgeClass: string;
  /** The second line under a badge, when the badge alone does not say enough. */
  why: string | null;
  /** The button the row offers, and its label; null when the row offers none. */
  action: TaxDocumentAction | null;
  actionLabel: string | null;
};

const valuesToReview = (count: number) =>
  count === 1 ? '1 value to review' : `${count} values to review`;

/**
 * The line under the badge of a document being read again after a reading of it failed: which
 * attempt this one is, out of how many the api makes, and the document's id.
 *
 * extractionAttempts counts the readings already made, so this one is that number plus one.
 * TAX_DOCUMENT_MAX_READ_ATTEMPTS is the number the api stops at (MAX_ATTEMPTS in
 * api/src/services/taxProfileExtraction/pipeline.ts is assigned from it), so the sentence cannot
 * promise a reading the api will not make.
 */
const readingAgainSentence = (
  document: Pick<TaxDocumentListItem, 'id' | 'extractionAttempts'>
): string =>
  `Attempt ${
    document.extractionAttempts + 1
  } of ${TAX_DOCUMENT_MAX_READ_ATTEMPTS}. ` +
  `If it keeps failing, send us document number ${document.id}.`;

/**
 * The badge of a document the check before promotion held: the
 * document was read, nothing from it was recorded, and a person has to say where it belongs.
 */
export const ENTITY_HOLD_BADGE = 'Confirm company';

/** The labels of the buttons a held document's row offers (entityHoldButtons below). */
export const ENTITY_HOLD_WORDS = {
  openClient: 'Open that client',
  addAnyway: 'Add to this company anyway',
  moveThere: 'Move there',
  keepHere: 'Keep here',
} as const;

/** What the toast says when the api refused an answer about a held document for no stated reason. */
export const ENTITY_ANSWER_FAILED = 'The answer could not be saved';

/**
 * The sentence under the badge of a held document, one per reason it was held.
 *
 * Expects the document's entityHold as the api sends it. The client or entity it names is null when
 * the api's lookup no longer finds the same one, and the sentence then names none.
 */
export const entityHoldSentence = (hold: TaxDocumentEntityHold): string => {
  switch (hold.status) {
    case 'held_other_client':
      return hold.client
        ? `This document belongs to ${hold.client.client_name} (another client). ` +
            'Nothing was added to this profile.'
        : "This document's EIN belongs to another client.";
    case 'held_ein_on_other_entity':
      return hold.legalEntity
        ? `This is ${hold.legalEntity.entity_name}'s document: its EIN matches that company's. ` +
            'Nothing was added here.'
        : "This document's EIN belongs to another of this client's companies.";
    case 'held_ein_not_on_entity':
      return "The EIN on this document is not this company's. Nothing was added yet.";
    case 'held_name_matches_other_entity':
      return hold.legalEntity
        ? `This looks like ${hold.legalEntity.entity_name}'s document.`
        : "This document names another of this client's companies.";
    case 'held_name_not_on_entity':
      return hold.documentName
        ? `This document names ${hold.documentName}, not this company.`
        : 'This document names a different company.';
    default: {
      // Every reason is a case above, so TypeScript fails here when the api adds one.
      const unhandled: never = hold;
      return unhandled;
    }
  }
};

/**
 * One button of a held document's row: either an answer the entity-answer route takes, or
 * switching the page to the other client the document's EIN belongs to.
 */
export type EntityHoldButton =
  | { kind: 'answer'; label: string; answer: AnswerTaxDocumentEntityRequest }
  | {
      kind: 'open-client';
      label: string;
      client: { id: number; client_name: string };
    };

/**
 * The buttons a held document's row offers, in the order they are drawn, by the reason it was held.
 *
 * "Add to this company anyway" and "Keep here" both send keep_here; "Move there" sends move with
 * the entity the document's EIN or name matches. A button that needs a client or an entity is left
 * out when the api sent none.
 */
export const entityHoldButtons = (
  hold: TaxDocumentEntityHold
): EntityHoldButton[] => {
  const keepHere = (label: string): EntityHoldButton => ({
    kind: 'answer',
    label,
    answer: { answer: 'keep_here' },
  });
  /** "Move there" to the entity the api named, then "Keep here"; "Keep here" alone without one. */
  const moveThereOrKeepHere = (
    legalEntity: { id: number } | null
  ): EntityHoldButton[] => [
    ...(legalEntity
      ? [
          {
            kind: 'answer' as const,
            label: ENTITY_HOLD_WORDS.moveThere,
            answer: { answer: 'move' as const, entityId: legalEntity.id },
          },
        ]
      : []),
    keepHere(ENTITY_HOLD_WORDS.keepHere),
  ];
  switch (hold.status) {
    case 'held_other_client':
      return [
        ...(hold.client
          ? [
              {
                kind: 'open-client' as const,
                label: ENTITY_HOLD_WORDS.openClient,
                client: hold.client,
              },
            ]
          : []),
        keepHere(ENTITY_HOLD_WORDS.addAnyway),
      ];
    case 'held_ein_not_on_entity':
      return [keepHere(ENTITY_HOLD_WORDS.addAnyway)];
    case 'held_ein_on_other_entity':
    case 'held_name_matches_other_entity':
      return moveThereOrKeepHere(hold.legalEntity);
    case 'held_name_not_on_entity':
      return [keepHere(ENTITY_HOLD_WORDS.keepHere)];
    default: {
      // Every reason is a case above, so TypeScript fails here when the api adds one.
      const unhandled: never = hold;
      return unhandled;
    }
  }
};

/**
 * What a document's row says in its Status and Action columns.
 *
 * Expects the document's row as the api sends it. onedrive_status, extraction_status,
 * extraction_error, extractionAttempts, doc_type, intake_channel and the stored file name decide
 * the badge; waitingCount says how many values of its latest reading wait for a person.
 *
 * Returns the badge's text and its daisyUI modifier class, the sentence under the badge or null,
 * and the button the row offers with its label, both null where the row offers no button.
 */
export const documentStatusWording = (
  document: TaxDocumentListItem
): TaxDocumentStatusWording => {
  const none = { why: null, action: null, actionLabel: null };

  if (document.onedrive_status === 'not_in_onedrive') {
    return { text: 'Waiting for the file', badgeClass: 'badge-ghost', ...none };
  }

  switch (document.extraction_status) {
    case 'queued':
    case 'processing':
      // A reading sent again after a failure looks like a first reading on the row, so
      // extractionAttempts is what tells a slow first reading from one that has failed before.
      if (document.extractionAttempts > 0) {
        return {
          text: 'Reading again after an error',
          badgeClass: 'badge-warning',
          why: readingAgainSentence(document),
          action: null,
          actionLabel: null,
        };
      }
      return {
        text: 'Reading the document',
        badgeClass: 'badge-info',
        ...none,
      };
    case 'extracted': {
      // A held document was read and nothing from it was recorded, so it has no values to review:
      // the row asks where it belongs instead. badge-warning rather than badge-error, because
      // nothing broke and a person has to answer.
      if (document.entityHold) {
        return {
          text: ENTITY_HOLD_BADGE,
          badgeClass: 'badge-warning',
          why: entityHoldSentence(document.entityHold),
          action: null,
          actionLabel: null,
        };
      }
      if (document.waitingCount > 0) {
        return {
          text: valuesToReview(document.waitingCount),
          badgeClass: 'badge-warning',
          ...none,
        };
      }
      // Nothing of the reading waits, which is either every value decided or a reading that gave
      // nothing new; the row no longer carries what tells the two apart, so it claims neither.
      return {
        text: 'Read, nothing to review',
        badgeClass: 'badge-ghost',
        ...none,
      };
    }
    case 'failed': {
      // This row is waiting for a person to say what the file is, not holding a file that broke,
      // so it takes badge-ghost rather than the badge-error the other failures take. Its button
      // opens ChooseDocumentTypeModal and sends nothing, so it is labelled for that question.
      if (document.extraction_error === 'unknown_document_kind') {
        return {
          text: 'Needs a document type',
          badgeClass: 'badge-ghost',
          why: readFailureSentence(document),
          action: 'choose-type',
          actionLabel: 'Add document type',
        };
      }
      const retryable =
        document.extraction_error !== null &&
        RETRYABLE_ERROR_CODES.includes(document.extraction_error);
      return {
        text: 'Could not be read',
        badgeClass: 'badge-error',
        why: readFailureSentence(document),
        action: retryable ? 'retry' : null,
        actionLabel: retryable ? 'Try again' : null,
      };
    }
    case 'duplicate':
      return {
        text: 'Same file already attached',
        badgeClass: 'badge-ghost',
        ...none,
      };
    case 'not_requested':
    default: {
      // A file added from the dashboard with no type chosen. The reading works the type out from
      // the file itself, so the row offers its button although doc_type is empty. Every other row
      // with an empty doc_type came from an onboarding form question that maps to no type — the
      // payroll report, the spreadsheet of states, the org chart, "anything else" — and those are
      // filed and never read, which is why intake_channel is checked as well as doc_type.
      const typeToBeWorkedOut =
        document.doc_type === null && document.intake_channel === 'manual';
      if (!typeHasBeenChosen(document) && !typeToBeWorkedOut) {
        return { text: 'On file', badgeClass: 'badge-ghost', ...none };
      }
      // A .xls or a Word document of a type the profile reads: stored, never sent to be read.
      const fileName =
        document.original_filename ?? document.onedrive_filename ?? '';
      if (!taxDocumentFileIsReadable(fileName)) {
        return {
          text: 'On file',
          badgeClass: 'badge-ghost',
          why: FILED_ONLY_SENTENCE,
          action: null,
          actionLabel: null,
        };
      }
      return {
        text: 'Ready to read',
        badgeClass: 'badge-ghost',
        why: typeToBeWorkedOut ? TYPE_WORKED_OUT_FROM_THE_FILE : null,
        action: 'read',
        actionLabel: 'Parse document',
      };
    }
  }
};

/**
 * The document's type label, or its stored file name where it has no type: the rule in
 * shared/utils/taxDocumentName.ts, which the api names a value's source document with too.
 */
export const documentTitle = (
  document: Pick<TaxDocumentListItem, 'doc_type' | 'onedrive_filename'>
): string =>
  taxDocumentTitle({
    docType: document.doc_type,
    onedriveFilename: document.onedrive_filename,
  });

/**
 * What the left column says while the Add document modal's request is in flight: how the files
 * were chosen (the modal's AddDocumentChoice, repeated here so wording.ts imports no component)
 * and how many of them there are.
 */
export const addInFlightSentence = (
  count: number,
  choice: 'pick' | 'upload'
): string => {
  const files = count === 1 ? '1 file' : `${count} files`;
  return choice === 'upload' ? `Uploading ${files}…` : `Adding ${files}…`;
};

/** What the left column says while the api is still reading documents. */
export const readingSentence = (count: number): string => {
  const documents = count === 1 ? '1 document' : `${count} documents`;
  return `Reading ${documents}`;
};

/**
 * One finished reading as the left column names it: the document's type label and the sentence its
 * row's badge carries, joined by a colon, as in 'Federal Tax Package: 4 values to review'. The
 * badge's sentence starts a line of its own in the table, so its first letter is lowered here.
 */
export const documentOutcomeLine = (document: TaxDocumentListItem): string => {
  const { text } = documentStatusWording(document);
  const lowered = `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
  return `${documentTitle(document)}: ${lowered}`;
};

/**
 * The Added column: the date and time the row was created, in the reader's own time zone, then who
 * or what put it there. The time is there because several files are often added on the same
 * day, and the day alone does not tell two of them apart.
 */
export const addedSentence = (document: TaxDocumentListItem): string => {
  const when = formatDateTime(document.created_at);
  if (document.intake_channel.startsWith('typeform')) {
    return `${when} · onboarding form`;
  }
  const who = formatPersonName(document.addedBy);
  return who ? `${when} · ${who}` : when;
};

/**
 * The title of the Added cell: the same moment with its seconds, so two files added within the same
 * minute can still be told apart.
 */
export const addedTooltip = (
  document: Pick<TaxDocumentListItem, 'created_at'>
): string => formatDateTime(document.created_at, true);

/**
 * The small line under the stored file name on a document's summary screen, which says the name the
 * file arrived under: null when the copy in the client's Files folder kept that name, and null when
 * the row holds no name the file arrived under.
 */
export const addedAsSentence = (
  document: Pick<TaxDocumentListItem, 'original_filename' | 'onedrive_filename'>
): string | null => {
  const original = document.original_filename;
  if (!original || original === document.onedrive_filename) return null;
  return `Added as ${original}`;
};

/**
 * Where in the document a value was read, as the summary screen says it.
 *
 * Expects the number the reading answered with, and whether the document is a spreadsheet. A
 * spreadsheet is sent to be read as CSV text with one block per sheet and its sheet number comes
 * back in the same field a PDF's page number does, so the caller decides which of the two words to
 * ask for by the file's name (taxDocumentFileIsSpreadsheet, shared constants).
 *
 * Returns 'Sheet 3' or 'Page 3', and null where the reading gave no number.
 */
export const pageHint = (
  page: number | null,
  isSpreadsheet: boolean
): string | null => {
  if (page === null) return null;
  return isSpreadsheet ? `Sheet ${page}` : `Page ${page}`;
};

/**
 * A label with the tax year its value is for: 'Capital share (2024)'. The label alone when the
 * year is null.
 */
export const labelForYear = (label: string, taxYear: number | null): string =>
  taxYear === null ? label : `${label} (${taxYear})`;

/**
 * The line under the title of a document's summary screen: the tax year the document is for and
 * when it arrived and from whom (addedSentence).
 */
export const summaryYearAndAddedLine = (
  document: TaxDocumentListItem
): string => {
  const year =
    document.doc_year === null
      ? 'No tax year'
      : `Tax year ${document.doc_year}`;
  return `${year} · Added ${addedSentence(document)}`;
};

/**
 * What the summary screen calls each status of a value a document gave: the short badge of its
 * line and the words of the count line. Only 'waiting' asks anything of a person.
 */
export const DOCUMENT_VALUE_STATUS_WORDS: Readonly<
  Record<TaxDocumentValueStatus, { badge: string; sentence: string }>
> = {
  waiting: { badge: 'Needs review', sentence: 'Needs review' },
  accepted: { badge: 'Accepted', sentence: 'Accepted into the profile' },
  same_as_approved: {
    badge: 'Matches profile',
    sentence: 'Same as the approved value',
  },
  not_used: {
    badge: 'Not used',
    sentence: 'Not used: a person chose another value',
  },
  replaced: { badge: 'Replaced', sentence: 'Replaced later' },
  not_captured: {
    badge: 'Not captured',
    sentence: 'Not captured: nothing was recorded for this answer',
  },
};

/** The statuses in the order the count line names them. */
export const DOCUMENT_VALUE_STATUS_ORDER: ReadonlyArray<TaxDocumentValueStatus> =
  [
    'waiting',
    'accepted',
    'same_as_approved',
    'not_used',
    'replaced',
    'not_captured',
  ];

/** The words of each status in the count labels, which follow a number: one form for 1, one for more. */
const COUNT_LABEL_WORDS: Readonly<
  Record<TaxDocumentValueStatus, { one: string; many: string }>
> = {
  waiting: { one: 'needs review', many: 'need review' },
  accepted: { one: 'accepted', many: 'accepted' },
  same_as_approved: { one: 'matches the profile', many: 'match the profile' },
  not_used: { one: 'not used', many: 'not used' },
  replaced: { one: 'replaced later', many: 'replaced later' },
  not_captured: { one: 'not captured', many: 'not captured' },
};

/** "1 needs review", "2 need review": a number of values of one status, as its count label says it. */
export const countOfStatus = (
  status: TaxDocumentValueStatus,
  count: number
): string =>
  `${count} ${
    count === 1 ? COUNT_LABEL_WORDS[status].one : COUNT_LABEL_WORDS[status].many
  }`;

/**
 * The small labels at the top of a document's summary screen, one per status with a value, in the
 * order waiting first: "3 need review", "8 accepted", "1 matches the profile". Empty when there is
 * no line at all, where the screen says NO_VALUES_SENTENCE instead for a document that was read.
 */
export const summaryCountLabels = (
  counts: Record<TaxDocumentValueStatus, number>
): { status: TaxDocumentValueStatus; text: string }[] =>
  DOCUMENT_VALUE_STATUS_ORDER.filter((status) => (counts[status] ?? 0) > 0).map(
    (status) => ({ status, text: countOfStatus(status, counts[status]) })
  );

/** What the summary screen says about a document that was read and gave no value. */
export const NO_VALUES_SENTENCE = 'This document gave no values.';

/** What the summary screen says while its answer is on its way, and when it could not be read. */
export const SUMMARY_LOADING = 'Reading what the document gave';
export const SUMMARY_FAILED = 'What this document gave could not be read';

/** The link on a line of the summary screen that closes it and shows the value on the page. */
export const GO_TO_FIELD = 'Go to field';

/** The heading of a summary screen's group of a list's values: the table's name on the page. */
export const LIST_GROUP_LABELS: Readonly<Record<TaxProfileListName, string>> = {
  owners: 'Owners',
  officers: 'Officers',
  subsidiaries: 'Subsidiaries',
  stateTaxIds: 'State registrations',
};

/** The kind of a list row, before the status in the header of its card on the summary screen. */
export const SUMMARY_CARD_KINDS: Readonly<Record<TaxProfileListName, string>> =
  Object.fromEntries(
    Object.entries(LIST_ROW_NOUNS).map(([list, noun]) => [
      list,
      noun.charAt(0).toUpperCase() + noun.slice(1),
    ])
  ) as Record<TaxProfileListName, string>;

/**
 * The status on the right of a list row's card on the summary screen: "Owner · all reviewed" when
 * none of the row's lines waits, "Owner · 1 needs review", "Officer · 2 need review".
 */
export const summaryCardStatus = (
  list: TaxProfileListName,
  waitingCount: number
): string =>
  `${SUMMARY_CARD_KINDS[list]} · ${
    waitingCount === 0 ? 'all reviewed' : countOfStatus('waiting', waitingCount)
  }`;

/** The label of the one line the summary screen draws a row's street, city, state and ZIP code on. */
export const SUMMARY_ADDRESS_LABEL = 'Address';

/**
 * The text of that line: "300 Elm Avenue, Salem, OR 97301", with the second address line after the
 * street when there is one. A part the document did not give is left out.
 */
export const summaryAddressText = (parts: {
  street?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
}): string => {
  const stateAndZip = [parts.state, parts.zip].filter(Boolean).join(' ');
  return [parts.street, parts.line2, parts.city, stateAndZip]
    .filter(Boolean)
    .join(', ');
};

/**
 * The Field cell of a line of the summary screen: the label, the tax year of a per-year value, and
 * the list row the value is on — 'Ownership (2024) — Jane Doe', 'Total assets (2024)', 'EIN'.
 */
export const summaryLineLabel = (
  line: Pick<TaxDocumentReviewLine, 'label' | 'taxYear' | 'rowName'>
): string => {
  const label = labelForYear(line.label, line.taxYear);
  return line.rowName ? `${label} — ${line.rowName}` : label;
};

/**
 * The small line under a status badge that says who decided and when: 'Approved by Ana Ruiz on
 * Sep 10, 2026' for a value accepted into the profile, 'Decided by Ana Ruiz on Sep 12, 2026' for a
 * candidate a person closed, 'Replaced on Sep 14, 2026'. Null when nothing is recorded.
 */
export const summaryDecisionLine = (
  line: Pick<TaxDocumentReviewLine, 'status' | 'decidedBy' | 'decidedAt'>
): string | null => {
  if (line.status === 'replaced') {
    return line.decidedAt
      ? `Replaced on ${formatTimestampDate(line.decidedAt)}`
      : null;
  }
  const who = personAndDate(line.decidedBy, line.decidedAt);
  if (!who) return null;
  if (line.status === 'accepted') return `Approved by ${who}`;
  if (line.status === 'not_used' || line.status === 'same_as_approved') {
    return `Decided by ${who}`;
  }
  return null;
};

/**
 * The words of a document's summary screen (ReviewModal) that decide its waiting lines: the line
 * under ValuePicker's first sentence, the line over the buttons after Confirm review had values
 * the api refused, and the button that closes the screen.
 */
export const DOCUMENT_REVIEW_WORDS = {
  pickerNote: "Nothing is saved until you confirm this document's review.",
  refused: 'Some values could not be saved. Each one says why on its line.',
  close: 'Close',
} as const;

/**
 * The line under a waiting line of the summary screen whose preselected value (the newest
 * candidate, newestWaiting) came from another source than the document reviewed: "Newer value
 * from <document> (tax year 2024)", "Newer value from the onboarding form". The document's name is
 * a piece with its documentId, which SourceText draws as a link.
 */
export const newerValueLine = (
  source: TaxProfileValueSource,
  fieldKey: string
): SourcePiece[] => [
  plainPiece('Newer value from '),
  sourceNamePiece(source, fieldKey),
  ...(source.kind === 'document' && source.taxYear
    ? [plainPiece(` (tax year ${source.taxYear})`)]
    : []),
];

/**
 * The line at the top of the summary screen when the documents list has a later year's document of
 * the same kind for the same legal entity: "A newer Federal Tax Package (2024) is on file. Where it
 * gives a different value, the newer value is preselected." The newer document's name is a piece
 * with its id, which SourceText draws as a link.
 */
export const newerDocumentSentence = (
  newer: Pick<
    TaxDocumentListItem,
    'id' | 'doc_type' | 'onedrive_filename' | 'doc_year'
  >
): SourcePiece[] => [
  plainPiece('A newer '),
  {
    text: `${documentTitle(newer)}${
      newer.doc_year ? ` (${newer.doc_year})` : ''
    }`,
    documentId: newer.id,
  },
  plainPiece(
    ' is on file. Where it gives a different value, the newer value is preselected.'
  ),
];
